// Debug probe for raycasting between XR-space/world-space and the ZUpGroup (added 2026-09-22 during the z-up migration).
//
// Purpose: ImageFrameUI computes a ray (controllerPos -> first room-collider hit, then lerps toward the
// controller) in WORLD/root space, then converts it into the ZUpGroup's local space. When ray-derived
// placements look wrong, this probe shows each pipeline stage as world-space markers, so you can see
// exactly which stage diverges (ray origin/direction vs. hit point vs. final placement).
//
// Usage: mount <ImageFramesDebugProbe/> outside the ZUpGroup in LHQ/Scene.tsx (it's currently mounted,
// but INERT -- it renders nothing and does nothing until enabled), then in the browser console run:
//		g.debugImageFramesRay = true
// Markers:
//		red line    = ray (controllerPos -> controllerPos + controllerDir * 10)
//		green ball  = far-target (first collider hit, or controllerPos + dir*5)
//		blue ball   = final frame pos, in world space (lerp result, pre-local-conversion)
//		yellow ball = groupRef's actual world position (converted back to world)
// All balls should sit on the red line; yellow/blue should coincide, at the place the frame should be.
// Also dumps all values to console once per second while enabled.
import {useFrame} from "@react-three/fiber";
import React, {useRef} from "react";
import * as THREE from "three";
import {LHQScene_latest} from "../../LHQ/Scene.js";
import {rayData} from "./ImageFrameUI.js";

export const ImageFramesDebugProbe = ()=>{
	const lineRef = useRef<THREE.Line|null>(null);
	const farBallRef = useRef<THREE.Mesh|null>(null);
	const finalBallRef = useRef<THREE.Mesh|null>(null);
	const frameBallRef = useRef<THREE.Mesh|null>(null);
	const lastLogTime = useRef(0);

	useFrame(()=>{
		// always kept in react-tree, but rendered only while the flag is set (can't just return null from here -- the three-objects are created imperatively, and useFrame can't trigger re-renders)
		const active = !!g.debugImageFramesRay;
		for (const obj of [lineRef.current, farBallRef.current, finalBallRef.current, frameBallRef.current]) {
			if (obj) obj.visible = active;
		}
		if (!active) return;

		const lhq = LHQScene_latest;
		if (lhq == null) return;

		// grab the same inputs ImageFrameUI uses (recomputed there, in world space)
		const {controllerPos, controllerDir} = rayData;
		if (controllerPos == null || controllerDir == null) return;

		// red line: origin -> origin + dir * 10
		if (lineRef.current) {
			const geom = lineRef.current.geometry as THREE.BufferGeometry;
			const posAttr = geom.attributes.position as THREE.BufferAttribute;
			posAttr.setXYZ(0, controllerPos.x, controllerPos.y, controllerPos.z);
			posAttr.setXYZ(1, controllerPos.x + controllerDir.x * 10, controllerPos.y + controllerDir.y * 10, controllerPos.z + controllerDir.z * 10);
			posAttr.needsUpdate = true;
		}

		// green ball: far-target
		if (farBallRef.current) {
			farBallRef.current.position.copy(rayData.farTarget ?? controllerPos);
		}
		// blue ball: final lerped pos (world)
		if (finalBallRef.current) {
			finalBallRef.current.position.copy(rayData.finalPosWorld ?? controllerPos);
		}
		// yellow ball: where the frame actually is, in world space
		const groupRef = rayData.groupRef;
		if (frameBallRef.current && groupRef?.current) {
			groupRef.current.getWorldPosition(frameBallRef.current.position);
		}

		// per-second dump
		if (Date.now() - lastLogTime.current > 1000) {
			lastLogTime.current = Date.now();
			console.log("[ImageFramesDebugProbe]", {
				controllerPos: controllerPos.toArray().map(a=>a.toFixed(2)),
				controllerDir: controllerDir.toArray().map(a=>a.toFixed(2)),
				farTarget: rayData.farTarget?.toArray().map(a=>a.toFixed(2)),
				finalPosWorld: rayData.finalPosWorld?.toArray().map(a=>a.toFixed(2)),
				frameWorldPos: frameBallRef.current?.position.toArray().map(a=>a.toFixed(2)),
			});
		}
	});

	return <>
		{/* note: lowercase <line> collides with the SVG DOM element in react's JSX typing; use uppercase <line_ ThreeJS> workaround via extend-free primitive instead */}
		<primitive object={(() => {
			const geom = new THREE.BufferGeometry();
			geom.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
			const mat = new THREE.LineBasicMaterial({color: "red"});
			const line = new THREE.Line(geom, mat);
			line.frustumCulled = false; // position-attr updates per frame; without this, three culls using the stale (all-zero) bounding sphere		line.visible = false; // start hidden; useFrame toggles visibility based on g.debugImageFramesRay			lineRef.current = line;
			return line;
		})()}/>
		{/* note: balls use default depth-testing; if a ball seems "missing", it's likely just occluded by the frame-quad/skybox (the blue ball sits exactly where the frame is placed).
		   Also note the probe renders in the MAIN pass, so VR layer compositing (vrLayers/LHQOverlay hud-pass) can hide it inXR -- check non-XR first. */}
		<mesh ref={finalBallRef}>
			<sphereGeometry args={[.08]}/>
			<meshBasicMaterial color="blue"/>
		</mesh>
		<mesh ref={frameBallRef}>
			<sphereGeometry args={[.05]}/>
			<meshBasicMaterial color="yellow"/>
		</mesh>
		<mesh ref={farBallRef}>
			<sphereGeometry args={[.05]}/>
			<meshBasicMaterial color="green"/>
		</mesh>
	</>;
};
